import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { undoToast } from "@/components/admin/UndoButton";
import { readJson } from "./adminApi";

export const useClassroom = (authFetch, enabled, onUndone) => {
  const [batch, setBatch] = useState(null);
  const [state, setState] = useState(null);
  const [nextTuesday, setNextTuesday] = useState("");
  const [classroom, setClassroom] = useState(null);
  const [saving, setSaving] = useState(false);
  const [busy, setBusy] = useState(false);

  const savedRef = useRef(null);
  const load = useCallback(async () => {
    if (!enabled) return;
    try {
      const [b, c] = await Promise.all([authFetch("/admin/batch/current"), authFetch("/admin/classroom")]);
      if (b.ok) { const d = await b.json(); setBatch(d.batch); setState(d.state); setNextTuesday(d.next_tuesday); }
      if (c.ok) { const cj = await c.json(); setClassroom(cj); savedRef.current = cj; }
    } catch (err) {
      console.warn("classroom load failed", err);
    }
  }, [authFetch, enabled]);

  useEffect(() => { load(); }, [load]);

  const setField = (k, v) => setClassroom((c) => ({ ...c, [k]: v }));
  const setVideo = (day, k, v, field = "videos") => setClassroom((c) => ({ ...c, [field]: { ...c[field], [day]: { ...(c[field] || {})[day], [k]: v } } }));
  const setVideoMany = (day, patch, field = "videos") => setClassroom((c) => ({ ...c, [field]: { ...c[field], [day]: { ...(c[field] || {})[day], ...patch } } }));
  const slotLabel = (day, field = "videos") => (field === "videos" && day === "6" ? "Day 6 - Part 1" : day === "6b" ? "Day 6 - Part 2" : `Day ${day}`);
  const upKey = (day, field) => (field === "videos10" ? `t${day}` : day);
  const removeVideo = async (day, field = "videos") => {
    if (!window.confirm(`Remove the ${slotLabel(day, field)} video? The day will show "video being prepared" until you add another.`)) return;
    const cur = await readJson(await authFetch("/admin/classroom"));
    const videos = { ...(cur?.[field] || {}), [day]: { ...((cur?.[field] || {})[day] || {}), url: "", source: "link", filename: "", size: 0 } };
    const res = await authFetch("/admin/classroom", { method: "PUT", body: JSON.stringify({ [field]: videos }) });
    if (!res.ok) { toast.error("Could not remove"); return; }
    setVideoMany(day, { url: "", source: "link", filename: "", size: 0 }, field);
    setUploads((u) => ({ ...u, [upKey(day, field)]: null }));
    toast.success(`${slotLabel(day, field)} video removed`);
  };
  const setOverride = (day, v) => setClassroom((c) => ({ ...c, day_overrides: { ...c.day_overrides, [day]: v } }));

  const save = async () => {
    setSaving(true);
    try {
      const res = await authFetch("/admin/classroom", { method: "PUT", body: JSON.stringify(classroom) });
      const data = await readJson(res);
      if (!res.ok) throw new Error(data?.detail || "Failed");
      setClassroom(data);
      toast.success("Classroom settings saved");
      load();
    } catch (err) {
      toast.error(err.message || "Could not save classroom");
    } finally {
      setSaving(false);
    }
  };

  const FIELD_TOAST = {
    guides_can_view: (v) => (v ? "Videos are now visible to Guides" : "Videos hidden from Guides"),
    visible: (v) => (v ? "Classroom is visible to the batch" : "Classroom hidden from the batch"),
    all_day: (v) => (v ? "Videos now play 24 hours on their day" : "Videos follow the daily start / cut-off window"),
    classroom_started: (v) => (v ? "Present Batch started — participants and Guides can now enter" : "Present Batch stopped — videos are not accessible"),
    login_password: (v) => (v ? "Log in to Classroom password saved" : "Log in to Classroom password cleared — mobile number only"),
    home_ui: (v) => (v === "timeline" ? "Website now shows the Timeline (Option B) steps" : "Website now shows the Classic steps"),
    home_link_password: (v) => (v ? "Classroom password saved" : "Classroom password cleared — mobile number only"),
    home_link_visible: (v) => (v ? "“Enter the classroom” link is now visible on the website" : "“Enter the classroom” link hidden on the website"),
    home_audience: (v) => (v.length ? `Register Steps ON for ${v.map((a) => (a === "present" ? "Present Batch" : "Guides")).join(" & ")}` : "Register Steps OFF for everyone — website shows the simple Register view"),
    master_control: (v) => (v === "stop" ? "Master Control STOP — classroom locked, all access switched OFF (settings remembered)" : "Master Control Open — remembered settings restored"),
    home_steps: (v) => `Register Steps shown on website: ${["1", "2", "3"].filter((k) => v[k] !== false).map((k) => `Step ${k}`).join(", ") || "none"}`,
    participants_mode: (v) => ({ always: "Participants can play any video any time", day: "Participants: that day's video, 24 hours (IST)", slot: "Participants: that day's video in the time slot · play once, no pause / rewind / fast-forward" })[v],
    guides_mode: (v) => ({ always: "Guides can play any video any time", day: "Guides: that day's video, 24 hours (IST)", slot: "Guides: that day's video in the time slot" })[v],
  };

  const saveField = async (k, v) => {
    setClassroom((c) => ({ ...c, [k]: k === "screen" ? { ...(c.screen || {}), ...Object.fromEntries(Object.entries(v).map(([sk, sv]) => [sk, { ...((c.screen || {})[sk] || {}), ...sv }])) } : v }));
    try {
      const res = await authFetch("/admin/classroom", { method: "PUT", body: JSON.stringify({ [k]: v }) });
      const data = await readJson(res);
      if (!res.ok) throw new Error(data?.detail || "Failed");
      setClassroom(data);
      toast.success(FIELD_TOAST[k] ? FIELD_TOAST[k](v) : "Saved");
    } catch (err) {
      toast.error(err.message || "Could not update");
      load();
    }
  };

  const [uploads, setUploads] = useState({});
  const uploadVideo = async (day, file, field = "videos") => {
    if (!file) return;
    if (!file.type.startsWith("video/")) { toast.error("Please choose a video file"); return; }
    const setP = (p) => setUploads((u) => ({ ...u, [upKey(day, field)]: p }));
    setP({ pct: 0, label: "Starting…" });
    try {
      const initRes = await authFetch("/admin/classroom/upload/init", { method: "POST", body: JSON.stringify({ filename: file.name, content_type: file.type, size: file.size }) });
      const init = await readJson(initRes);
      if (!initRes.ok) throw new Error(init?.detail || "Could not start upload");
      const total = Math.ceil(file.size / init.chunk_size);
      for (let i = 0; i < total; i++) {
        const fd = new FormData();
        fd.append("chunk", file.slice(i * init.chunk_size, (i + 1) * init.chunk_size));
        const r = await authFetch(`/admin/classroom/upload/${init.upload_id}/chunk?index=${i}`, { method: "POST", body: fd, headers: {} });
        if (!r.ok) throw new Error((await readJson(r))?.detail || `Chunk ${i + 1} failed`);
        setP({ pct: Math.round(((i + 1) / total) * 95), label: `Uploading ${Math.round(((i + 1) / total) * 100)}%` });
      }
      setP({ pct: 97, label: "Saving to storage…" });
      const doneRes = await authFetch(`/admin/classroom/upload/${init.upload_id}/complete`, { method: "POST" });
      const done = await readJson(doneRes);
      if (!doneRes.ok) throw new Error(done?.detail || "Could not finish upload");
      const cur = await readJson(await authFetch("/admin/classroom"));
      const meta = { url: done.url, source: "upload", filename: done.filename, size: done.size };
      const videos = { ...(cur?.[field] || {}), [day]: { ...((cur?.[field] || {})[day] || {}), ...meta, title: (cur?.[field] || {})[day]?.title || slotLabel(day, field) } };
      const saveRes = await authFetch("/admin/classroom", { method: "PUT", body: JSON.stringify({ [field]: videos }) });
      if (!saveRes.ok) throw new Error("Uploaded, but could not save the link — paste it manually: " + done.url);
      setClassroom((c) => ({ ...c, [field]: { ...c[field], [day]: { ...((c[field] || {})[day] || {}), ...meta, title: (c[field] || {})[day]?.title || slotLabel(day, field) } } }));
      setP({ pct: 100, label: `Uploaded ${file.name}` });
      toast.success(`${slotLabel(day, field)} video uploaded and saved`);
    } catch (err) {
      setP({ pct: 0, label: "", error: err.message || "Upload failed" });
      toast.error(err.message || "Upload failed");
    }
  };

  const commence = async (startDate, onDone, ids) => {
    const who = ids?.length ? `the ${ids.length} selected participant(s)` : "all registered participants";
    if (!window.confirm(`Commence the batch starting ${startDate} (Day 1)? ${who} move to the Present Batch and leave the registration table.`)) return;
    setBusy(true);
    try {
      const res = await authFetch("/admin/batch/commence", { method: "POST", body: JSON.stringify({ start_date: startDate, ids: ids?.length ? ids : undefined }) });
      const data = await readJson(res);
      if (!res.ok) throw new Error(data?.detail || "Failed");
      undoToast(authFetch, `Batch ${data.batch.batch_no} commenced with ${data.batch.participants.length} participants`, data, () => { load(); onUndone?.(); });
      await load();
      onDone?.();
    } catch (err) {
      toast.error(err.message || "Could not commence batch");
    } finally {
      setBusy(false);
    }
  };

  const setBatchNo = async (no) => {
    if (!no?.trim()) return;
    try {
      const res = await authFetch("/admin/batch/current", { method: "PUT", body: JSON.stringify({ batch_no: no.trim() }) });
      const data = await readJson(res);
      if (!res.ok) throw new Error(data?.detail || "Failed");
      toast.success(`Running batch number set to ${no.trim()}`);
      await load();
    } catch (err) {
      toast.error(err.message || "Could not change the batch number");
    }
  };

  const setBatchDate = async (date) => {
    if (!date) return;
    try {
      const res = await authFetch("/admin/batch/current", { method: "PUT", body: JSON.stringify({ start_date: date }) });
      const data = await readJson(res);
      if (!res.ok) throw new Error(data?.detail || "Failed");
      toast.success(`Day 1 date set to ${date}`);
      await load();
    } catch (err) {
      toast.error(err.message || "Could not change the batch date");
    }
  };

  const saveFields = async (keys, msg) => {
    setSaving(true);
    try {
      const body = Object.fromEntries(keys.filter((k) => classroom[k] !== undefined).map((k) => [k, classroom[k]]));
      const res = await authFetch("/admin/classroom", { method: "PUT", body: JSON.stringify(body) });
      const data = await readJson(res);
      if (!res.ok) throw new Error(data?.detail || "Failed");
      setClassroom(data);
      savedRef.current = data;
      toast.success(msg || "Saved");
      load();
    } catch (err) {
      toast.error(err.message || "Could not save");
    } finally {
      setSaving(false);
    }
  };
  const isDirty = (keys) => !!savedRef.current && keys.some((k) => (classroom?.[k] ?? "") !== (savedRef.current?.[k] ?? ""));

  const resetSchedule = async (who) => {
    try {
      const res = await authFetch(`/admin/classroom/reset-schedule/${who}`, { method: "POST" });
      const data = await readJson(res);
      if (!res.ok) throw new Error(data?.detail || "Failed");
      setClassroom(data);
      const r = data?._reset;
      const when = r?.date ? new Date(`${r.date}T00:00:00`).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" }) : "no date";
      const fmt = (t) => { const [h, m] = (t || "").split(":").map(Number); const ap = h >= 12 ? "PM" : "AM"; return `${((h + 11) % 12) + 1}:${String(m || 0).padStart(2, "0")} ${ap}`; };
      toast.success(`${who === "guides" ? "Guides" : "Participants"} reset to the website announcement: ${when}, ${fmt(r?.start_time)}–${fmt(r?.end_time)} IST${r?.from_website ? "" : " (announcement not readable — defaults used)"}`);
      await load();
    } catch (err) {
      toast.error(err.message || "Could not reset");
    }
  };

  const [pEditing, setPEditing] = useState(null);
  const [pForm, setPForm] = useState({});
  const [pSaving, setPSaving] = useState(false);
  const startParticipantEdit = (p) => {
    setPEditing(p);
    setPForm({ name: p.name || "", mobile: p.mobile || "", whatsapp: p.whatsapp || "", email: p.email || "", profession: p.profession || "", place: p.place || "", program: p.program || "", batch: p.batch || "" });
  };
  const startParticipantAdd = () => {
    setPEditing({ id: "new" });
    setPForm({ name: "", mobile: "", whatsapp: "", email: "", profession: "", place: "", program: "Positivelife", batch: batch?.batch_no || "" });
  };
  const saveParticipant = async (e) => {
    e.preventDefault();
    setPSaving(true);
    const isNew = pEditing?.id === "new";
    try {
      const res = await authFetch(isNew ? "/admin/batch/participants/create" : `/admin/batch/participants/${pEditing.id}`, { method: isNew ? "POST" : "PUT", body: JSON.stringify(pForm) });
      const data = await readJson(res);
      if (!res.ok) throw new Error(data?.detail || "Failed");
      undoToast(authFetch, isNew ? "Participant added to the Present Batch" : "Participant updated", data, () => { load(); onUndone?.(); });
      setPEditing(null);
      await load();
    } catch (err) {
      toast.error(err.message || "Save failed");
    } finally {
      setPSaving(false);
    }
  };

  const moveParticipants = async (path, ids, okMsg, onDone) => {
    setBusy(true);
    try {
      const res = await authFetch(path, { method: "POST", body: JSON.stringify({ ids }) });
      const data = await readJson(res);
      if (!res.ok) throw new Error(data?.detail || "Failed");
      undoToast(authFetch, okMsg(data), data, () => { load(); onUndone?.(); });
      await load();
      onDone?.();
    } catch (err) {
      toast.error(err.message || "Could not move participants");
    } finally {
      setBusy(false);
    }
  };
  const archiveParticipants = (ids, onDone) => moveParticipants("/admin/batch/participants/archive", ids, (d) => `Empty all: ${d.moved} participant(s) moved to Past Batch`, onDone);
  const deleteParticipant = async (p) => {
    if (!window.confirm(`Are you sure you want to delete ${p.name} from the Present Batch? (You can Undo afterwards.)`)) return;
    setBusy(true);
    try {
      const res = await authFetch(`/admin/batch/participants/${p.id}`, { method: "DELETE" });
      const data = await readJson(res);
      if (!res.ok) throw new Error(data?.detail || "Failed");
      undoToast(authFetch, `${p.name} deleted from the Present Batch`, data, () => { load(); onUndone?.(); });
      await load();
    } catch (err) {
      toast.error(err.message || "Delete failed");
    } finally {
      setBusy(false);
    }
  };
  const addParticipants = (ids, onDone) => moveParticipants("/admin/batch/participants/add", ids, (d) => `${d.moved} participant(s) moved to Present Batch (Batch ${d.batch_no}, ${d.participants} total)`, onDone);
  const returnParticipants = (ids, onDone) => moveParticipants("/admin/batch/participants/return", ids, (d) => `${d.moved} participant(s) moved back to Future Batch`, onDone);

  const resetSession = async (p) => {
    if (!window.confirm(`Reset the device login for ${p.name}? They will be able to log in again from any one device.`)) return;
    try {
      const res = await authFetch(`/admin/batch/participants/${p.id}/reset-session`, { method: "POST" });
      if (!res.ok) throw new Error((await readJson(res))?.detail || "Failed");
      toast.success(`Device login reset for ${p.name}`);
      await load();
    } catch (err) { toast.error(err.message || "Failed"); }
  };

  const [sel, setSel] = useState([]);
  const [accessScope, setAccessScope] = useState("all");
  const setParticipantsAccess = async (ids, enabled) => {
    if (!window.confirm(`Are you sure you want to ${enabled ? "enable" : "disable"} the classroom for the ${ids.length} selected participant(s)?`)) return;
    setBusy(true);
    try {
      const res = await authFetch("/admin/batch/participants/access", { method: "POST", body: JSON.stringify({ ids, enabled }) });
      const data = await readJson(res);
      if (!res.ok) throw new Error(data?.detail || "Failed");
      undoToast(authFetch, `Classroom ${enabled ? "enabled" : "disabled"} for ${data.updated} selected participant(s)`, data, () => { load(); onUndone?.(); });
      await load();
    } catch (err) {
      toast.error(err.message || "Failed");
    } finally {
      setBusy(false);
    }
  };

  const close = async () => {
    if (!window.confirm(`Are you sure you want to close batch ${batch?.batch_no || ""}? ALL ${batch?.participants?.length || 0} participant(s) move to Past Batch and are removed from the Present Batch.`)) return;
    setBusy(true);
    try {
      const res = await authFetch("/admin/batch/close", { method: "POST" });
      const data = await readJson(res);
      if (!res.ok) throw new Error(data?.detail || "Failed");
      undoToast(authFetch, `Batch closed · ${data.archived} participant(s) moved to Past Batch`, data, () => { load(); onUndone?.(); });
      await load();
    } catch (err) {
      toast.error(err.message || "Could not close batch");
    } finally {
      setBusy(false);
    }
  };

  return { batch, state, nextTuesday, classroom, setField, saveField, setVideo, setOverride, save, saving, commence, close, busy, reload: load, addParticipants, returnParticipants, archiveParticipants, deleteParticipant, setBatchDate, setBatchNo, resetSchedule, saveFields, isDirty, pEditing, setPEditing, pForm, setPForm, pSaving, startParticipantEdit, startParticipantAdd, saveParticipant, uploadVideo, uploads, setVideoMany, removeVideo, sel, setSel, accessScope, setAccessScope, setParticipantsAccess, resetSession, authFetch };
};
